import { languageLabel } from './languageLabel';
/** Position-only metadata. Cases never persist this object. */
export const COMMITMENTS = ['full_time', 'part_time', 'as_needed', 'to_be_determined'];
export const PAY_BASES = ['all_work', 'billable_only', 'separate_rates', 'to_be_determined'];
/** Optional travel requirement. Absent = not specified (nothing is shown). */
export const TRAVEL_OPTIONS = ['none', 'up_to_25', 'up_to_50', 'over_50'];
/** RBT roles only: must the provider already hold the RBT credential? Absent = not specified. */
export const CERTIFICATIONS = ['required', 'preferred', 'not_required'];
/** Guaranteed weekly hours, as stored (validated 1..60 by public.valid_position_details). */
export const MAX_GUARANTEED_HOURS = 60;
export const MAX_TRAINING_RATE = 500;
export const PRACTICE_STATES = 'AL AK AZ AR CA CO CT DE FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY DC PR VI GU'.split(' ');
/** Legacy: old rows may carry a count. It is no longer collected or shown (one confirmed hire fills the listing); it defaults to 1 and still validates. */
export const MAX_OPENINGS = 50;
/** Staff language needs for the ROLE (cases.languages is the client's language and stays empty). */
export const LANGUAGE_LEVELS = ['required', 'preferred'];
export const MAX_ROLE_LANGUAGES = 5;

/** The shared W2 perk list a posting offers (cases and positions); positions add or remove a few (see positionBenefitOptions). */
export const BASE_BENEFIT_OPTIONS = ["Health Insurance", "Dental & Vision", "401k", "PTO (10 days)", "PTO (15 days)", "CEU Reimbursement", "Sign-on Bonus", "Referral Bonus", "Productivity / Billable Bonus", "School Calendar Schedule", "Flexible Schedule", "BCBA Fieldwork Hours"];

/** Extra perks only positions offer, on top of the shared W2 benefit list. */
export const POSITION_EXTRA_BENEFITS = [
  'Paid Holidays', 'BACB / License Fees Paid', 'Company Laptop & Phone',
  'Professional Liability Insurance', 'Life & Disability Insurance', 'Fieldwork Supervision Opportunities',
];
/** Extra perks for an RBT staff position (on top of the shared W2 list, which already has 401k, referral bonus and BCBA fieldwork hours). */
export const RBT_POSITION_EXTRA_BENEFITS = ['Paid Holidays', 'Life & Disability Insurance'];
/** What a 1099 RBT position may advertise. */
export const RBT_POSITION_CONTRACTOR_BENEFITS = [
  'Flexible Schedule', 'Productivity / Billable Bonus', 'Referral Bonus', 'BCBA Fieldwork Hours', 'Professional Liability Insurance',
];
/**
 * What a 1099 (contractor) position may advertise. W2 employment keeps the full list; a
 * contractor is paid an hourly rate, so only perks that make sense without payroll benefits
 * stay: a productivity bonus, equipment, liability cover, fee reimbursement, and supervision
 * opportunities.
 */
export const POSITION_CONTRACTOR_BENEFITS = [
  'Flexible Schedule', 'Productivity / Billable Bonus', 'Company Laptop & Phone', 'Professional Liability Insurance',
  'BACB / License Fees Paid', 'Fieldwork Supervision Opportunities',
];

/** One of TRAVEL_OPTIONS, or undefined. Never null, so an unset travel never reaches the payload. */
const normalizeTravel = raw => TRAVEL_OPTIONS.includes(raw) ? raw : undefined;

/** A finite number above 0 (and at most `max`), else undefined. Never null: an unset value never reaches the payload. */
function normalizeAmount(raw, max) {
  if (raw == null || String(raw).trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 && n <= max ? n : undefined;
}
const normalizeCertification = raw => CERTIFICATIONS.includes(raw) ? raw : undefined;

const text = value => typeof value === 'string' ? value.trim() : '';

function normalizeOpenings(raw) {
  if (raw == null || String(raw).trim() === '') return 1;
  const n = Number(raw);
  return Number.isFinite(n) ? n : text(String(raw));
}

function normalizeLanguages(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const out = [];
  for (const item of raw) {
    const lang = text(item?.lang);
    if (!lang || seen.has(lang.toLowerCase())) continue;
    seen.add(lang.toLowerCase());
    out.push({ lang, level: text(item?.level) || 'preferred' });
  }
  return out;
}

export function normalizePositionDetails(details = {}, form = {}) {
  const d = details || {};
  const annual = form.payPeriod === 'annual';
  const payBasis = annual ? 'salary' : d.payBasis === 'salary' ? '' : text(d.payBasis);
  // An RBT position is always in person and RBTs hold no state license: it never stores practice or license states.
  const rbt = form.role === 'RBT';
  const stateRelevant = !rbt && ['remote', 'hybrid'].includes(form.deliveryMode);
  const statesToBeDetermined = stateRelevant && d.statesToBeDetermined === true;
  // Remote/hybrid: the practice states. In person: an optional "state license required" list (BCBA only).
  const requiredStates = !rbt && !statesToBeDetermined && Array.isArray(d.requiredStates)
    ? [...new Set(d.requiredStates.map(s => text(s).toUpperCase()).filter(Boolean))] : [];
  const rawRate = payBasis === 'separate_rates' ? d.nonBillableRate : null;
  const rate = rawRate == null || String(rawRate).trim() === '' ? null : Number(rawRate);
  const travel = normalizeTravel(d.travel);
  // RBT-only recruiting facts. A BCBA position never carries them.
  const certification = rbt ? normalizeCertification(d.certification) : undefined;
  const guaranteedHours = rbt ? normalizeAmount(d.guaranteedHours, MAX_GUARANTEED_HOURS) : undefined;
  // A training (pre-certification) rate only exists when certification is not strictly required, and is hourly.
  const trainingRate = rbt && certification !== 'required' && !annual ? normalizeAmount(d.trainingRate, MAX_TRAINING_RATE) : undefined;
  const cancellationPay = rbt && d.cancellationPay === true ? true : undefined;
  return {
    jobTitle: text(d.jobTitle), timeCommitment: text(d.timeCommitment), payBasis,
    nonBillableRate: rate == null ? null : Number.isFinite(rate) ? rate : text(rawRate),
    openings: normalizeOpenings(d.openings),
    languages: normalizeLanguages(d.languages),
    requiredStates, statesToBeDetermined,
    supportNotes: text(d.supportNotes), qualifications: text(d.qualifications),
    ...(travel ? { travel } : {}),
    ...(guaranteedHours ? { guaranteedHours } : {}),
    ...(cancellationPay ? { cancellationPay } : {}),
    ...(trainingRate ? { trainingRate } : {}),
    ...(certification ? { certification } : {}),
  };
}

export function positionDetailsErrors(form, section = 'all') {
  const d = normalizePositionDetails(form.positionDetails, form);
  const errors = [];
  if (section === 'all' || section === 'identity') {
    if (!d.jobTitle || d.jobTitle.length > 120) errors.push('Enter a position title of 1–120 characters.');
    if (!COMMITMENTS.includes(d.timeCommitment)) errors.push('Select full-time, part-time, as needed, or to be determined.');
    if (!(form.employments || []).some(e => ['W2', '1099'].includes(e))) errors.push('Select W2 and/or 1099 for this position.');
    if (!Number.isInteger(d.openings) || d.openings < 1 || d.openings > MAX_OPENINGS)
      errors.push(`Enter the number of openings, from 1 to ${MAX_OPENINGS}.`);
  }
  if (section === 'all' || section === 'schedule') {
    if (d.requiredStates.some(s => !PRACTICE_STATES.includes(s))) errors.push('Select valid practice states.');
    if (['remote', 'hybrid'].includes(form.deliveryMode) && !d.requiredStates.length && !d.statesToBeDetermined)
      errors.push('Select the practice states, or mark them to be determined.');
    if (form.role === 'RBT') {
      const raw = form.positionDetails?.guaranteedHours;
      if (raw != null && String(raw).trim() !== '' && d.guaranteedHours == null)
        errors.push(`Guaranteed hours must be a number from 1 to ${MAX_GUARANTEED_HOURS}, or leave blank.`);
      const expected = Number(form.hours);
      if (d.guaranteedHours != null && form.hours !== '' && form.hours != null && Number.isFinite(expected) && expected > 0 && d.guaranteedHours > expected)
        errors.push('Guaranteed hours cannot be more than the expected weekly hours.');
    }
  }
  if (section === 'all' || section === 'pay') {
    if (form.payPeriod !== 'annual' && !PAY_BASES.includes(d.payBasis)) errors.push('Specify what the hourly rate covers, or select to be determined.');
    if (d.payBasis === 'separate_rates' && (!Number.isFinite(d.nonBillableRate) || d.nonBillableRate <= 0))
      errors.push('Provide a positive non-billable hourly rate for separate rates.');
    if (form.role === 'RBT' && d.certification !== 'required') {
      const raw = form.positionDetails?.trainingRate;
      if (raw != null && String(raw).trim() !== '' && d.trainingRate == null && form.payPeriod !== 'annual')
        errors.push('The training rate must be a positive hourly amount, or leave blank.');
    }
  }
  if (section === 'all' || section === 'skills' || section === 'pay') {
    if (d.supportNotes.length > 2000 || d.qualifications.length > 2000) errors.push('Keep support and qualification notes to 2,000 characters each.');
  }
  if (section === 'all' || section === 'skills') {
    if (d.languages.length > MAX_ROLE_LANGUAGES) errors.push(`List at most ${MAX_ROLE_LANGUAGES} languages.`);
    if (d.languages.some(l => l.lang.length > 40 || !LANGUAGE_LEVELS.includes(l.level))) errors.push('Mark each language as required or preferred.');
  }
  return errors;
}

/** Benefits a position may carry, given its employment type(s): W2 keeps everything, contractors a short list. */
export function positionBenefitsFor(benefits, employments, role) {
  const list = benefits || [];
  const contractor = role === 'RBT' ? RBT_POSITION_CONTRACTOR_BENEFITS : POSITION_CONTRACTOR_BENEFITS;
  return (employments || []).includes('W2') ? list : list.filter(b => contractor.includes(b));
}

/** Every perk an RBT/BCBA position may offer, given its employment type(s). Order is display order. */
export function positionBenefitOptions(role, employments, baseW2Options = []) {
  const w2 = (employments || []).includes('W2');
  if (role === 'RBT') return w2 ? [...baseW2Options, ...RBT_POSITION_EXTRA_BENEFITS] : RBT_POSITION_CONTRACTOR_BENEFITS;
  return w2 ? [...baseW2Options.filter(b => b !== 'BCBA Fieldwork Hours'), ...POSITION_EXTRA_BENEFITS] : POSITION_CONTRACTOR_BENEFITS;
}

/** "35 guaranteed hours a week"; null when not specified. */
export function positionGuaranteedSummary(details, t) {
  const n = Number(details?.guaranteedHours);
  return Number.isFinite(n) && n > 0 ? t('position.guaranteedSummary', { hours: n }) : null;
}
export const positionCancellationSummary = (details, t) => details?.cancellationPay === true ? t('position.cancellationPaySummary') : null;
/** "$20/hr until certified"; null when not specified. */
export function positionTrainingRateSummary(details, t) {
  const n = Number(details?.trainingRate);
  return Number.isFinite(n) && n > 0 ? t('position.trainingRateSummary', { rate: n }) : null;
}
/** "RBT certification required" / "preferred" / "not required, we help you certify"; null when not specified. */
export function positionCertificationSummary(details, t) {
  const c = normalizeCertification(details?.certification);
  return c ? t(`position.certification.summary.${c}`) : null;
}

/**
 * Soft notice for a provider with NO credential on their profile looking at an RBT listing.
 * A case: "case" (it typically expects an RBT certification ID). A position: only when the position
 * REQUIRES the certification ("position"); preferred or not required (the agency helps a new hire
 * certify) says nothing. null = no notice. A provider who holds any credential gets none.
 */
export function rbtCertificationNotice(listing, credential) {
  if (listing?.type !== 'RBT' || String(credential || '').trim()) return null;
  const position = (listing?.listingKind || listing?.listing_kind) === 'position';
  if (!position) return 'case';
  return normalizeCertification(listing?.positionDetails?.certification) === 'required' ? 'position' : null;
}

/** Joins summary parts with commas, lowercasing the first letter of each part after the first ("35 hours a week, paid for ..."). */
export function joinSummaryParts(parts) {
  return parts.filter(Boolean).map((p, i) => i === 0 ? p : p.charAt(0).toLocaleLowerCase() + p.slice(1)).join(', ');
}

/** The ages a position's program serves ({min, max}) or null. Not client data: a program attribute. */
export function agesServed(listing) {
  const rawMin = listing?.ageMin ?? listing?.age_min, rawMax = listing?.ageMax ?? listing?.age_max;
  if (rawMin == null || rawMax == null || rawMin === '' || rawMax === '') return null;
  const min = Number(rawMin), max = Number(rawMax);
  return Number.isFinite(min) && Number.isFinite(max) && min > 0 && max >= min ? { min, max } : null;
}

/** "No travel" / "Up to 25% travel" / "Up to 50% travel" / "More than 50% travel"; null when not specified. */
export function positionTravelSummary(details, t) {
  const travel = normalizeTravel(details?.travel);
  return travel ? t(`position.travel.summary.${travel}`) : null;
}

export function positionPaySummary(details, t) {
  const basis = details?.payBasis;
  if (!basis || basis === 'salary') return null;
  return basis === 'separate_rates'
    ? t('position.paySeparateSummary', { rate: details.nonBillableRate })
    : t(`position.payBasis.${basis}`);
}

/** "Spanish preferred" / "Spanish required"; "Bilingual Spanish preferred" for a single non-English language. */
export function positionLanguageSummary(details, t) {
  const langs = Array.isArray(details?.languages) ? details.languages : [];
  if (!langs.length) return null;
  // "Bilingual" only reads right for a second language; English preferred is just "English preferred".
  const key = (lang, level) => level === 'required' ? 'required' : (String(lang).toLowerCase() === 'english' ? 'preferredPlain' : 'preferred');
  return langs.map(({ lang, level }) => t(`position.languageSummary.${key(lang, level)}`, { lang: languageLabel(t, lang, { inline: true }) })).join(', ');
}

/** Soft, never-excluding language evidence against a provider profile (profile.languages). */
export function assessPositionLanguages(details, profile) {
  const needs = Array.isArray(details?.languages) ? details.languages : [];
  if (!needs.length) return null;
  const spoken = new Set((profile?.languages || []).map(l => String(l).toLowerCase()));
  const has = ({ lang }) => spoken.has(String(lang).toLowerCase());
  return {
    requiredMissing: needs.filter(n => n.level === 'required' && !has(n)).map(n => n.lang),
    preferredMatched: needs.filter(n => n.level === 'preferred' && has(n)).map(n => n.lang),
    requiredMatched: needs.filter(n => n.level === 'required' && has(n)).map(n => n.lang),
  };
}
